import { apiDelete, apiGet, apiPost, apiPut, type RequestOptions } from './index'

export interface ClockMetaItemType {
  label?: string | null
  color_token?: string | null
  default_duration_seconds?: number | null
}

export interface ClockMetaResponse {
  item_types?: Record<string, ClockMetaItemType> | null
  request_id?: string
}

export interface ClockTemplateItem {
  id: number
  template_id: number
  position: number
  type: string
  title: string | null
  default_duration_seconds: number
  auto_advance: boolean
  note: string | null
  is_fixed_time: boolean
  fixed_time_at: string | null
  created_at?: string
  updated_at?: string
}

export interface ClockTemplateSummary {
  id: number
  station_id: number
  name: string
  notes: string | null
  item_count: number
  estimated_duration_seconds?: number | null
  last_used_at: string | null
  updated_at: string | null
  created_at?: string | null
}

export interface ClockTemplateDetail extends ClockTemplateSummary {
  items: ClockTemplateItem[]
}

export interface ClockTemplateListResponse {
  templates: ClockTemplateSummary[]
  page: number
  per_page: number
  total: number
  request_id?: string
}

export interface ListClockTemplatesParams {
  stationId: number
  page?: number
  perPage?: number
  search?: string
}

export interface ClockTemplateItemInput {
  id?: number
  type: string
  title: string | null
  default_duration_seconds: number
  auto_advance?: boolean | null
  note?: string | null
  is_fixed_time?: boolean
  fixed_time_at?: string | null
  position: number
}

export interface ClockTemplateInput {
  station_id: number
  name: string
  notes?: string | null
  items: ClockTemplateItemInput[]
}

export interface ClockTemplateResponse {
  template: ClockTemplateDetail
  request_id?: string
}

export interface ClockTemplateMutationResponse {
  template: ClockTemplateDetail
  request_id?: string
}

export interface ClockTemplateDeleteResponse {
  success: boolean
  request_id?: string
}

export interface ClockMetaParams {
  stationId: number
}

export async function getClockMeta(
  params: ClockMetaParams,
  options?: RequestOptions,
): Promise<ClockMetaResponse> {
  const search = new URLSearchParams({ station_id: String(params.stationId) })
  return apiGet<ClockMetaResponse>(`clock/meta?${search.toString()}`, options)
}

export async function listClockTemplates(
  params: ListClockTemplatesParams,
  options?: RequestOptions,
): Promise<ClockTemplateListResponse> {
  const search = new URLSearchParams({
    station_id: String(params.stationId),
  })

  if (params.page && params.page > 1) {
    search.set('page', String(params.page))
  }

  if (params.perPage && params.perPage > 0) {
    search.set('per_page', String(params.perPage))
  }

  if (params.search) {
    search.set('search', params.search)
  }

  return apiGet<ClockTemplateListResponse>(`clock/templates?${search.toString()}`, options)
}

export async function getClockTemplate(
  stationId: number,
  templateId: number,
  options?: RequestOptions,
): Promise<ClockTemplateDetail> {
  const search = new URLSearchParams({ station_id: String(stationId) })
  const response = await apiGet<ClockTemplateResponse>(
    `clock/templates/${templateId}?${search.toString()}`,
    options,
  )

  if (!response || !response.template) {
    throw new Error('Template response is missing.')
  }

  return response.template
}

export async function createClockTemplate(
  payload: ClockTemplateInput,
  options?: RequestOptions,
): Promise<ClockTemplateMutationResponse> {
  return apiPost<ClockTemplateMutationResponse>('clock/templates', payload, options)
}

export async function updateClockTemplate(
  templateId: number,
  payload: ClockTemplateInput,
  options?: RequestOptions,
): Promise<ClockTemplateMutationResponse> {
  return apiPut<ClockTemplateMutationResponse>(`clock/templates/${templateId}`, payload, options)
}

export async function deleteClockTemplate(
  stationId: number,
  templateId: number,
  options?: RequestOptions,
): Promise<ClockTemplateDeleteResponse> {
  const search = new URLSearchParams({ station_id: String(stationId) })
  return apiDelete<ClockTemplateDeleteResponse>(`clock/templates/${templateId}?${search.toString()}`, options)
}

export async function duplicateClockTemplate(
  stationId: number,
  templateId: number,
  options?: RequestOptions,
): Promise<ClockTemplateMutationResponse> {
  const search = new URLSearchParams({ station_id: String(stationId) })
  return apiPost<ClockTemplateMutationResponse>(
    `clock/templates/${templateId}/duplicate?${search.toString()}`,
    undefined,
    options,
  )
}
